import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import clsx from "clsx";
import { CheckIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Checkbox, TextInput } from "@/components/ui/Field";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Spinner } from "@/components/ui/Spinner";
import { Autocomplete, type AutocompleteOption } from "@/components/Autocomplete";
import { useToast } from "@/components/ui/toast/useToast";
import { ApiError } from "@/lib/apiClient";
import {
  listGeschaeftspartnerObjektadressen,
  listKontaktKunden,
  type Geschaeftspartner,
  type Objektadresse,
} from "@/lib/kontakte";
import {
  contractLocationLabel,
  deleteMaintenanceContract,
  deleteMaintenanceContractDocument,
  getContractOptions,
  getHeroCustomerAddresses,
  getMaintenanceContract,
  heroAddressLabel,
  saveMaintenanceContract,
  uploadMaintenanceContractDocument,
  type ContractFormOption,
  type HeroCustomerAddress,
  type MaintenanceContractDocument,
  type MaintenanceType,
} from "@/lib/wartungen";
import { ContractDocumentCard } from "./components/ContractDocumentCard";
import { ContractStatusBadge } from "./components/ContractCard";
import { PeriodPicker } from "./components/PeriodPicker";
import { CONTRACT_STATUS_LABELS, contractDetailHref, type ContractStatusKey } from "./lib/campaignView";
import {
  ALL_PERIOD_UNITS,
  canonicalPeriod,
  durationLabel,
  frequencyLabel,
  INTERVAL_PRESETS,
  NOTICE_PERIOD_UNITS,
  NOTICE_PRESETS,
  RENEWAL_PRESETS,
  RENEWAL_TERM_UNITS,
  toPeriod,
  type Period,
} from "./lib/contractPeriod";
import { formatDate } from "./lib/wartungenUi";

interface SelectedLocation {
  value: string;
  label: string;
}

type Tone = "info" | "success" | "error" | "warning";
type ContractBasis = "contract" | "on_call";

const STATUS_ORDER: ContractStatusKey[] = ["draft", "active", "paused", "ended", "cancelled"];
const DEFAULT_INTERVAL: Period = { value: 1, unit: "years" };

function mappingKey(heroAddressId: string | number, typeKey: string): string {
  return `${String(heroAddressId).trim()}::${String(typeKey).trim()}`;
}

function Section({
  step,
  title,
  description,
  done,
  optional,
  children,
}: {
  step?: number;
  title: string;
  description?: string;
  done?: boolean;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-ios border border-separator bg-card p-4 shadow-ios sm:p-5">
      <div className="mb-4 flex items-start gap-3">
        {step != null ? (
          <span
            className={clsx(
              "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
              done ? "bg-status-green text-label-on-accent" : "bg-accent/10 text-accent",
            )}
            aria-hidden
          >
            {done ? <CheckIcon className="h-4 w-4" /> : step}
          </span>
        ) : null}
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-label">
            {title}
            {optional ? <span className="ml-2 text-xs font-medium text-label-tertiary">optional</span> : null}
          </h3>
          {description ? <p className="mt-0.5 text-xs text-label-secondary">{description}</p> : null}
        </div>
      </div>
      {children}
    </section>
  );
}

function ChecklistItem({ done, label }: { done: boolean; label: string }) {
  return (
    <li className={clsx("flex items-center gap-2 text-sm", done ? "text-label" : "text-label-tertiary")}>
      <span
        className={clsx(
          "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border",
          done ? "border-status-green bg-status-green text-label-on-accent" : "border-separator-opaque",
        )}
        aria-hidden
      >
        {done ? <CheckIcon className="h-3 w-3" /> : null}
      </span>
      {label}
    </li>
  );
}

function contactLabel(entry: Geschaeftspartner): string {
  const company = String(entry.firmenname || "").trim();
  const person = `${String(entry.vorname || "").trim()} ${String(entry.nachname || "").trim()}`.trim();
  return company || person || `Kontakt #${entry.id}`;
}

function contactDescription(entry: Geschaeftspartner): string | undefined {
  const place = [entry.strasse, [entry.plz, entry.ort].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const number = String(entry.kundennummer || "").trim();
  const parts = [place, number ? `Nr. ${number}` : ""].filter(Boolean);
  return parts.length ? parts.join(" · ") : undefined;
}

function addressMatches(entry: Objektadresse, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return [entry.title, entry.street, entry.zipcode, entry.city]
    .map((part) => String(part || "").toLowerCase())
    .join(" ")
    .includes(needle);
}

function toHeroAddress(entry: Objektadresse): HeroCustomerAddress {
  return {
    address_id: entry.address_id,
    title: entry.title,
    street: entry.street,
    city: entry.city,
    deleted: Boolean(entry.deleted),
  };
}

function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <dt className="shrink-0 text-label-tertiary">{label}</dt>
      <dd className="min-w-0 truncate text-right text-label">{value}</dd>
    </div>
  );
}

export function VertragFormPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const rawId = (params.get("id") || "").trim();
  const contractId = rawId && Number(rawId) > 0 ? Number(rawId) : null;
  const isEdit = contractId != null;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [documentBusy, setDocumentBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ tone: Tone; message: string } | null>(null);

  const [maintenanceTypes, setMaintenanceTypes] = useState<MaintenanceType[]>([]);
  const [availableForms, setAvailableForms] = useState<ContractFormOption[]>([]);

  // Stammdaten
  const [basis, setBasis] = useState<ContractBasis>(params.get("basis") === "on_call" ? "on_call" : "contract");
  const [name, setName] = useState("");
  const [contractNumber, setContractNumber] = useState("");
  const [status, setStatus] = useState<ContractStatusKey>("draft");

  // Kunde, Orte, Kategorien
  const [heroCustomerId, setHeroCustomerId] = useState("");
  const [heroCustomerName, setHeroCustomerName] = useState("");
  const [geschaeftspartnerId, setGeschaeftspartnerId] = useState<number | null>(null);
  const [selectedLocations, setSelectedLocations] = useState<SelectedLocation[]>([]);
  const [selectedTypeKeys, setSelectedTypeKeys] = useState<string[]>([]);
  const [formMappings, setFormMappings] = useState<Map<string, string>>(new Map());

  // Laufzeit & Rhythmus
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [interval, setInterval_] = useState<Period | null>(DEFAULT_INTERVAL);
  const [autoRenew, setAutoRenew] = useState(false);
  const [notice, setNotice] = useState<Period | null>(null);
  const [renewal, setRenewal] = useState<Period | null>(null);
  const [planPlacement, setPlanPlacement] = useState(false);

  // Vertragsdokument
  const [document, setDocument] = useState<MaintenanceContractDocument | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFeedback(null);
    try {
      const options = await getContractOptions();
      setMaintenanceTypes(Array.isArray(options.maintenance_types) ? options.maintenance_types : []);
      setAvailableForms(Array.isArray(options.forms) ? options.forms : []);

      if (contractId != null) {
        const c = await getMaintenanceContract(contractId);
        setBasis(c.basis === "on_call" ? "on_call" : "contract");
        setName(String(c.name || "").trim());
        setContractNumber(String(c.contract_number || "").trim());
        setStatus(STATUS_ORDER.includes(c.status as ContractStatusKey) ? (c.status as ContractStatusKey) : "draft");

        const custId = String(c.hero_customer_id || "").trim();
        setHeroCustomerId(custId);
        let customerName =
          String(c.hero_customer_name || c.customer_name || "").trim() || (custId ? `Kunde #${custId}` : "");
        const heroNum = Number(custId);
        if (Number.isFinite(heroNum) && heroNum > 0) {
          try {
            const found = await listKontaktKunden({ hero_contact_id: heroNum, limit: 5 });
            const gp = (found.items || []).find((item) => item.is_deleted !== true);
            if (gp) {
              setGeschaeftspartnerId(gp.id);
              const appName = contactLabel(gp);
              if (appName) customerName = appName;
            } else {
              setGeschaeftspartnerId(null);
            }
          } catch {
            setGeschaeftspartnerId(null);
          }
        } else {
          setGeschaeftspartnerId(null);
        }
        setHeroCustomerName(customerName);

        const locs: SelectedLocation[] = [];
        for (const loc of Array.isArray(c.locations) ? c.locations : []) {
          const addrId = String(loc?.hero_address_id || "").trim();
          if (!addrId) continue;
          locs.push({ value: addrId, label: contractLocationLabel(loc, addrId) });
        }
        setSelectedLocations(locs);

        setStartDate(c.start_date || "");
        setEndDate(c.end_date || "");
        setInterval_(toPeriod(c.interval_value, c.interval_unit) ?? DEFAULT_INTERVAL);
        setAutoRenew(Boolean(c.auto_renew_enabled));
        setNotice(toPeriod(c.notice_period_value, c.notice_period_unit));
        setRenewal(toPeriod(c.renewal_term_value, c.renewal_term_unit));
        setPlanPlacement(Boolean(c.sub_location_plan_placement_required));
        setDocument(c.document ?? null);

        const typeKeys = Array.isArray(c.maintenance_types)
          ? c.maintenance_types.map((t) => String(t.key)).filter(Boolean)
          : Array.isArray(c.maintenance_type_keys)
            ? c.maintenance_type_keys.map((k) => String(k)).filter(Boolean)
            : [];
        setSelectedTypeKeys(typeKeys);

        const map = new Map<string, string>();
        for (const entry of Array.isArray(c.form_mappings) ? c.form_mappings : []) {
          const k = mappingKey(String(entry?.hero_address_id ?? ""), String(entry?.maintenance_type_key ?? ""));
          const fid = String(entry?.form_id ?? "").trim();
          if (k && fid && entry?.hero_address_id && entry?.maintenance_type_key) map.set(k, fid);
        }
        setFormMappings(map);
      }
    } catch (err) {
      setFeedback({
        tone: "error",
        message: err instanceof ApiError ? err.message : "Daten konnten nicht geladen werden.",
      });
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadCustomerOptions = useCallback(async (query: string): Promise<AutocompleteOption<Geschaeftspartner>[]> => {
    const text = query.trim();
    if (!text) return [];
    const found = await listKontaktKunden({ q: text, limit: 50 });
    return (found.items || [])
      .filter((entry) => entry.is_deleted !== true)
      .map((entry) => ({
        key: String(entry.id),
        label: contactLabel(entry),
        description: contactDescription(entry),
        value: entry,
      }))
      .filter((opt) => opt.key);
  }, []);

  const loadAddressOptions = useCallback(
    async (query: string): Promise<AutocompleteOption<HeroCustomerAddress>[]> => {
      if (!geschaeftspartnerId && !heroCustomerId) return [];
      const selectedIds = new Set(selectedLocations.map((l) => l.value));
      let list: HeroCustomerAddress[] = [];
      if (geschaeftspartnerId) {
        const addresses = await listGeschaeftspartnerObjektadressen(geschaeftspartnerId);
        list = addresses.filter((entry) => !entry.deleted && addressMatches(entry, query)).map(toHeroAddress);
      } else if (heroCustomerId) {
        list = (await getHeroCustomerAddresses(heroCustomerId, query)).filter((entry) => !entry.deleted);
      }
      return list
        .map((entry) => ({ key: String(entry.address_id), label: heroAddressLabel(entry), value: entry }))
        .filter((opt) => opt.key && !selectedIds.has(opt.key))
        .slice(0, 50);
    },
    [geschaeftspartnerId, heroCustomerId, selectedLocations],
  );

  function toggleType(key: string) {
    setSelectedTypeKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }

  function addLocation(addr: HeroCustomerAddress) {
    const value = String(addr.address_id).trim();
    if (!value) return;
    setSelectedLocations((prev) =>
      prev.some((l) => l.value === value) ? prev : [...prev, { value, label: heroAddressLabel(addr) || `Ort #${value}` }],
    );
  }

  function removeLocation(value: string) {
    setSelectedLocations((prev) => prev.filter((l) => l.value !== value));
  }

  const mappingRows = useMemo(() => {
    const rows: { locationId: string; locationLabel: string; typeKey: string; typeLabel: string }[] = [];
    for (const loc of selectedLocations) {
      for (const typeKey of selectedTypeKeys) {
        const type = maintenanceTypes.find((t) => String(t.key) === typeKey);
        rows.push({
          locationId: loc.value,
          locationLabel: loc.label,
          typeKey,
          typeLabel: String(type?.name || type?.name_plural || typeKey),
        });
      }
    }
    return rows;
  }, [selectedLocations, selectedTypeKeys, maintenanceTypes]);

  function setMapping(locationId: string, typeKey: string, formId: string) {
    setFormMappings((prev) => {
      const next = new Map(prev);
      const k = mappingKey(locationId, typeKey);
      if (formId) next.set(k, formId);
      else next.delete(k);
      return next;
    });
  }

  /* ───────────── Validierung & Zusammenfassung ───────────── */

  const isOnCall = basis === "on_call";
  const customerDone = Number(heroCustomerId) > 0;
  const locationsDone = selectedLocations.length > 0;
  const typesDone = selectedTypeKeys.length > 0;
  const intervalDone = Boolean(interval);
  const renewalDone = isOnCall || !autoRenew || (Boolean(notice) && Boolean(renewal));
  const dateOrderOk = isOnCall || !startDate || !endDate || startDate <= endDate;
  const formComplete = customerDone && locationsDone && typesDone && intervalDone && renewalDone && dateOrderOk;

  const blockingHint = geschaeftspartnerId != null && !customerDone
    ? "Dieser Kontakt ist nicht mit Hero verknüpft und kann keinem Vertrag zugeordnet werden."
    : !customerDone
    ? "Bitte einen Kunden aus den Kontakten wählen."
    : !locationsDone
      ? "Bitte mindestens einen Ort hinzufügen."
      : !typesDone
        ? "Bitte mindestens eine Wartungskategorie wählen."
        : !intervalDone
          ? "Bitte einen Wartungsrhythmus festlegen."
          : !renewalDone
            ? "Für die automatische Verlängerung bitte Kündigungsfrist und Verlängerungslaufzeit angeben."
            : !dateOrderOk
              ? "Das Enddatum liegt vor dem Startdatum."
              : null;

  const runtimeSentence = useMemo(() => {
    if (isOnCall) return `Wartung ${frequencyLabel(interval)}`;
    const parts: string[] = [];
    if (startDate && endDate) parts.push(`Laufzeit ${formatDate(startDate)} bis ${formatDate(endDate)}`);
    else if (startDate) parts.push(`Laufzeit ab ${formatDate(startDate)}, unbefristet`);
    else if (endDate) parts.push(`Laufzeit bis ${formatDate(endDate)}`);
    else parts.push("Unbefristete Laufzeit");
    parts.push(`Wartung ${frequencyLabel(interval)}`);
    if (autoRenew) {
      const r = renewal ? ` um ${durationLabel(renewal)}` : "";
      const n = notice ? `, wenn nicht ${durationLabel(notice)} vorher gekündigt wird` : "";
      parts.push(`verlängert sich automatisch${r}${n}`);
    } else if (endDate) {
      parts.push("endet ohne automatische Verlängerung");
    }
    return parts.join(" · ");
  }, [isOnCall, startDate, endDate, interval, autoRenew, renewal, notice]);

  const displayTitle =
    name.trim() ||
    (!isOnCall ? contractNumber.trim() : "") ||
    (isEdit ? (isOnCall ? "Auf Zuruf" : `Vertrag #${contractId}`) : isOnCall ? "Auf Zuruf" : "Neuer Wartungsvertrag");

  /* ───────────── Speichern, Löschen, Dokument ───────────── */

  function buildPayload(): Record<string, unknown> {
    const heroAddressIds = selectedLocations.map((l) => Number(l.value)).filter((v) => Number.isFinite(v) && v > 0);
    const mappingsPayload = mappingRows
      .map((row) => {
        const formId = formMappings.get(mappingKey(row.locationId, row.typeKey));
        const numericFormId = Number(formId);
        const heroAddressNum = Number(row.locationId);
        if (!formId || !Number.isFinite(numericFormId) || numericFormId <= 0) return null;
        if (!Number.isFinite(heroAddressNum) || heroAddressNum <= 0) return null;
        return { hero_address_id: heroAddressNum, maintenance_type_key: row.typeKey, form_id: numericFormId };
      })
      .filter((x): x is { hero_address_id: number; maintenance_type_key: string; form_id: number } => x != null);

    const intervalCanon = canonicalPeriod(interval ?? DEFAULT_INTERVAL, ALL_PERIOD_UNITS);
    const noticeCanon = autoRenew && notice ? canonicalPeriod(notice, NOTICE_PERIOD_UNITS) : null;
    const renewalCanon = autoRenew && renewal ? canonicalPeriod(renewal, RENEWAL_TERM_UNITS) : null;

    return {
      basis,
      name: name.trim() || null,
      contract_number: isOnCall ? null : contractNumber.trim() || null,
      hero_customer_id: Number(heroCustomerId),
      hero_address_ids: heroAddressIds,
      start_date: isOnCall ? null : startDate.trim() || null,
      end_date: isOnCall ? null : endDate.trim() || null,
      interval_value: intervalCanon.value,
      interval_unit: intervalCanon.unit,
      status,
      auto_renew_enabled: isOnCall ? false : autoRenew,
      notice_period_value: isOnCall ? null : noticeCanon?.value ?? null,
      notice_period_unit: isOnCall ? null : noticeCanon?.unit ?? null,
      renewal_term_value: isOnCall ? null : renewalCanon?.value ?? null,
      renewal_term_unit: isOnCall ? null : renewalCanon?.unit ?? null,
      sub_location_plan_placement_required: planPlacement,
      maintenance_type_keys: selectedTypeKeys,
      form_mappings: mappingsPayload,
    };
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!formComplete) {
      setFeedback({ tone: "error", message: blockingHint || "Bitte alle Pflichtangaben ausfüllen." });
      return;
    }

    setSaving(true);
    setFeedback(null);
    try {
      const saved = await saveMaintenanceContract(buildPayload(), contractId);
      const targetId = Number(saved?.id ?? contractId);

      if (!isOnCall && pendingFile && targetId > 0) {
        try {
          const res = await uploadMaintenanceContractDocument(targetId, pendingFile);
          setDocument(res.document);
          setPendingFile(null);
        } catch (err) {
          toast.error(
            err instanceof ApiError
              ? `Vertrag gespeichert, aber das PDF konnte nicht abgelegt werden: ${err.message}`
              : "Vertrag gespeichert, aber das PDF konnte nicht abgelegt werden.",
          );
        }
      }

      const savedLabel = isOnCall ? "Auf Zuruf" : "Vertrag";
      if (isEdit) {
        toast.success(`${savedLabel} gespeichert.`);
        setFeedback({ tone: "success", message: "Änderungen gespeichert." });
      } else if (targetId > 0) {
        toast.success(`${savedLabel} angelegt.`);
        navigate(contractDetailHref(targetId));
      }
    } catch (err) {
      setFeedback({
        tone: "error",
        message: err instanceof ApiError ? err.message : "Speichern ist fehlgeschlagen.",
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (contractId == null) return;
    const noun = isOnCall ? "Eintrag auf Zuruf" : "Wartungsvertrag";
    if (!window.confirm(`${noun} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) return;
    setSaving(true);
    try {
      await deleteMaintenanceContract(contractId);
      toast.success(isOnCall ? "Auf Zuruf gelöscht." : "Vertrag gelöscht.");
      navigate(isOnCall ? "/wartungen?tab=auf-zuruf" : "/wartungen?tab=vertraege");
    } catch (err) {
      setFeedback({
        tone: "error",
        message: err instanceof ApiError ? err.message : "Vertrag konnte nicht gelöscht werden.",
      });
      setSaving(false);
    }
  }

  async function handleDocumentUpload(file: File) {
    if (contractId == null) return;
    setDocumentBusy(true);
    try {
      const res = await uploadMaintenanceContractDocument(contractId, file);
      setDocument(res.document);
      toast.success("Vertragsdokument im InfoHub abgelegt.");
    } catch (err) {
      throw err instanceof ApiError ? new Error(err.message) : err;
    } finally {
      setDocumentBusy(false);
    }
  }

  async function handleDocumentRemove() {
    if (contractId == null) return;
    setDocumentBusy(true);
    try {
      const res = await deleteMaintenanceContractDocument(contractId);
      setDocument(null);
      if (res.warnings?.length) toast.warning(res.warnings.join(" "));
      else toast.success("Vertragsdokument entfernt.");
    } catch (err) {
      throw err instanceof ApiError ? new Error(err.message) : err;
    } finally {
      setDocumentBusy(false);
    }
  }

  /* ───────────── Render ───────────── */

  const saveButton = (
    <Button type="submit" form="vertrag-form" data-tour="wartung-speichern" disabled={saving || !formComplete} title={blockingHint || undefined}>
      {saving ? "Speichere …" : isEdit ? "Änderungen speichern" : isOnCall ? "Auf Zuruf anlegen" : "Vertrag anlegen"}
    </Button>
  );

  return (
    <AppShell
      title={isEdit ? displayTitle : isOnCall ? "Auf Zuruf anlegen" : "Neuen Wartungsvertrag anlegen"}
      subtitle={
        isEdit
          ? [
              !isOnCall && contractNumber.trim() && contractNumber.trim() !== displayTitle ? `Nr. ${contractNumber.trim()}` : "",
              isOnCall ? "Auf Zuruf bearbeiten" : "Wartungsvertrag bearbeiten",
            ]
              .filter(Boolean)
              .join(" · ")
          : isOnCall
            ? "Kunde, Orte und Kategorien für Wartungen ohne Wartungsvertrag. Erscheint nicht unter Verträge."
            : "Ein Vertrag legt fest, für welchen Kunden, an welchen Orten und in welchen Kategorien gewartet wird."
      }
      back={
        isEdit && contractId
          ? { to: contractDetailHref(contractId), label: isOnCall ? "zurück zur Übersicht" : "zurück zum Vertrag" }
          : {
              to: isOnCall ? "/wartungen?tab=auf-zuruf" : "/wartungen?tab=vertraege",
              label: isOnCall ? "zurück zu Auf Zuruf" : "zurück zu den Verträgen",
            }
      }
    >
      {feedback ? (
        <Alert
          variant={feedback.tone === "error" ? "error" : feedback.tone === "success" ? "success" : "info"}
          className="mb-4"
        >
          {feedback.message}
        </Alert>
      ) : null}

      {loading ? (
        <p className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner /> Lade Vertragsdaten …
        </p>
      ) : (
        <div className="grid gap-5 pb-24 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start lg:pb-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
          <form id="vertrag-form" className="min-w-0 space-y-5" onSubmit={handleSubmit}>
            <section className="rounded-ios border border-separator bg-card p-4 shadow-ios sm:p-5">
              <h3 className="text-base font-semibold text-label">Art</h3>
              <p className="mt-0.5 text-xs text-label-secondary">
                Legt fest, ob der Eintrag in der Vertragsliste erscheint.
              </p>
              <div className="mt-3">
                <SegmentedControl<ContractBasis>
                  name="Art"
                  value={basis}
                  onChange={setBasis}
                  options={[
                    { value: "contract", label: "Wartungsvertrag" },
                    { value: "on_call", label: "Auf Zuruf" },
                  ]}
                />
              </div>
              <p className="mt-2 text-xs text-label-secondary">
                {isOnCall
                  ? "Ohne Vertragsnummer, Laufzeit und PDF. Die Wartungen bleiben in der Übersicht, der Eintrag nicht unter Verträge."
                  : "Mit Vertragsnummer, Laufzeit und optionalem PDF. Erscheint im Tab Verträge."}
              </p>
            </section>

            <Section
              step={1}
              title={isOnCall ? "Bezeichnung" : "Vertrag"}
              description={
                isOnCall
                  ? "Der Name erscheint in der Übersicht und an den Wartungen."
                  : "Name und Nummer machen den Vertrag in Listen wiedererkennbar."
              }
              done={Boolean(name.trim() || (!isOnCall && contractNumber.trim()))}
            >
              <div className={clsx("grid gap-4", isOnCall ? "" : "sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]")}>
                <TextInput
                  id="vertrag-name"
                  label="Name"
                  placeholder={isOnCall ? "z. B. Müller GmbH – Brandschutztüren" : "z. B. Klinikum Nord – Brandschutztüren"}
                  value={name}
                  maxLength={200}
                  onChange={(e) => setName(e.target.value)}
                  hint="Frei wählbar; wird als Titel in Übersicht und Wartungen angezeigt."
                />
                {isOnCall ? null : (
                  <TextInput
                    id="vertrag-nummer"
                    label="Vertragsnummer"
                    placeholder="z. B. WV-2026-003"
                    value={contractNumber}
                    maxLength={100}
                    onChange={(e) => setContractNumber(e.target.value)}
                    hint="Nummer aus dem Vertragsdokument oder der Buchhaltung."
                  />
                )}
              </div>
              <div className="mt-4">
                <span className="eh-label text-xs">Status</span>
                <div className="mt-1.5">
                  <SegmentedControl<ContractStatusKey>
                    name="Status"
                    value={status}
                    onChange={setStatus}
                    options={STATUS_ORDER.map((key) => ({ value: key, label: CONTRACT_STATUS_LABELS[key] }))}
                  />
                </div>
                <p className="mt-1.5 text-xs text-label-secondary">
                  {status === "active"
                    ? isOnCall
                      ? "Aktiv: daraus lassen sich Wartungen anlegen."
                      : "Aktive Verträge erscheinen in der Übersicht zuerst; neue Wartungen werden daraus angelegt."
                    : status === "draft"
                      ? isOnCall
                        ? "Entwurf: angelegt, aber noch nicht für Wartungen gedacht."
                        : "Entwurf: der Vertrag ist angelegt, aber noch nicht in Kraft."
                      : status === "paused"
                        ? "Pausiert: vorübergehend keine Wartungen geplant."
                        : isOnCall
                          ? "Beendet oder storniert: bleibt zur Historie erhalten."
                          : "Beendete oder stornierte Verträge bleiben zur Historie erhalten."}
                </p>
              </div>
            </Section>

            <Section
              step={2}
              title="Kunde & Orte"
              description="Der Vertrag gehört zu einem Kunden aus den Kontakten und gilt für einen oder mehrere seiner Orte."
              done={customerDone && locationsDone}
            >
              <div className="grid gap-4 sm:grid-cols-2" data-tour="wartung-kunde">
                <Autocomplete<Geschaeftspartner>
                  label="Kunde"
                  required
                  placeholder="Kontakt suchen …"
                  emptyText="Kein Kontakt gefunden"
                  initialQuery={heroCustomerName}
                  loadOptions={loadCustomerOptions}
                  minChars={1}
                  onSelect={(opt) => {
                    if (opt) {
                      const heroId = Number(opt.value.hero_contact_id);
                      setGeschaeftspartnerId(opt.value.id);
                      setHeroCustomerId(Number.isFinite(heroId) && heroId > 0 ? String(heroId) : "");
                      setHeroCustomerName(opt.label);
                      setSelectedLocations([]);
                    } else {
                      setGeschaeftspartnerId(null);
                      setHeroCustomerId("");
                    }
                  }}
                />
                <Autocomplete<HeroCustomerAddress>
                  label="Ort hinzufügen"
                  placeholder={
                    geschaeftspartnerId || heroCustomerId ? "Ort suchen …" : "Bitte zuerst Kunde wählen"
                  }
                  emptyText="Kein Ort bei diesem Kontakt"
                  clearOnSelect
                  loadOptions={loadAddressOptions}
                  onSelect={(opt) => {
                    if (opt) addLocation(opt.value);
                  }}
                />
              </div>
              <div className="mt-3">
                <span className="eh-label text-xs">
                  Orte im Vertrag <span className="text-status-red">*</span>
                </span>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {selectedLocations.length === 0 ? (
                    <span className="text-sm text-label-tertiary">
                      {geschaeftspartnerId || heroCustomerId
                        ? "Noch kein Ort ausgewählt – oben einen Ort des Kontakts suchen und hinzufügen."
                        : "Bitte zuerst einen Kunden wählen, danach Orte hinzufügen."}
                    </span>
                  ) : (
                    selectedLocations.map((loc) => (
                      <span key={loc.value} className="inline-flex items-center gap-1.5 rounded-full bg-accent-soft py-1 pl-3 pr-1.5 text-sm text-accent">
                        {loc.label}
                        <button
                          type="button"
                          className="rounded-full p-0.5 text-accent/70 hover:bg-accent/10 hover:text-accent"
                          aria-label={`Ort ${loc.label} entfernen`}
                          onClick={() => removeLocation(loc.value)}
                        >
                          <XMarkIcon className="h-3.5 w-3.5" aria-hidden />
                        </button>
                      </span>
                    ))
                  )}
                </div>
              </div>
            </Section>

            <Section
              step={3}
              title="Wartungskategorien"
              description="Welche Arten von Wartungen umfasst der Vertrag? Jede Kategorie bringt eigene Objekte und ein eigenes Formular mit."
              done={typesDone}
            >
              {maintenanceTypes.length === 0 ? (
                <p className="text-sm text-label-secondary">
                  Noch keine Wartungskategorien vorhanden.{" "}
                  <Link to="/wartungen/einstellungen" className="font-medium text-accent hover:underline">
                    Kategorie in den Einstellungen anlegen
                  </Link>
                  .
                </p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {maintenanceTypes.map((type) => {
                    const key = String(type.key);
                    const checked = selectedTypeKeys.includes(key);
                    return (
                      <label
                        key={key}
                        className={clsx(
                          "flex cursor-pointer items-start gap-2 rounded-lg border p-3 transition",
                          checked ? "border-accent bg-accent-soft" : "border-separator hover:bg-fill-tertiary",
                        )}
                      >
                        <input type="checkbox" className="mt-0.5" checked={checked} onChange={() => toggleType(key)} />
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-label">{type.name || type.name_plural || key}</span>
                          <span className="block truncate text-xs text-label-tertiary">{key}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </Section>

            <Section
              step={4}
              title={isOnCall ? "Rhythmus" : "Laufzeit & Rhythmus"}
              description={
                isOnCall
                  ? "Wie oft typischerweise gewartet wird. Eine einzelne Wartung bekommt später ihren eigenen Termin."
                  : "Wie lange läuft der Vertrag, wie oft wird gewartet – und verlängert er sich von selbst?"
              }
              done={intervalDone && renewalDone && dateOrderOk}
            >
              {isOnCall ? null : (
                <div className="grid gap-4 sm:grid-cols-2">
                  <TextInput
                    id="vertrag-start"
                    type="date"
                    label="Beginn"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    hint="Leer lassen, wenn unbekannt."
                  />
                  <TextInput
                    id="vertrag-ende"
                    type="date"
                    label="Ende"
                    value={endDate}
                    min={startDate || undefined}
                    onChange={(e) => setEndDate(e.target.value)}
                    hint={endDate ? undefined : "Leer = unbefristet."}
                    error={!dateOrderOk ? "Das Ende liegt vor dem Beginn." : undefined}
                  />
                </div>
              )}

              <div className={isOnCall ? "" : "mt-5"}>
                <PeriodPicker
                  label="Wartungsrhythmus"
                  required
                  presets={INTERVAL_PRESETS}
                  units={ALL_PERIOD_UNITS}
                  value={interval}
                  onChange={(next) => setInterval_(next)}
                  preview={interval ? frequencyLabel(interval) : undefined}
                  hint="Wie oft eine Wartung fällig ist. „Jährlich“ entspricht 12 Monaten – es gibt nur eine Angabe dafür."
                />
              </div>

              {isOnCall ? null : (
              <div className="mt-5 rounded-lg border border-separator bg-fill-quaternary/30 p-3">
                <Checkbox
                  id="vertrag-autorenew"
                  label="Verlängert sich automatisch"
                  description="Der Vertrag läuft nach dem Ende weiter, wenn er nicht fristgerecht gekündigt wird."
                  checked={autoRenew}
                  onChange={(e) => setAutoRenew(e.target.checked)}
                />
                {autoRenew ? (
                  <div className="mt-4 grid gap-5 border-t border-separator pt-4 md:grid-cols-2">
                    <PeriodPicker
                      label="Kündigungsfrist"
                      required
                      presets={NOTICE_PRESETS}
                      units={NOTICE_PERIOD_UNITS}
                      value={notice}
                      onChange={setNotice}
                      hint="Spätester Kündigungszeitpunkt vor dem Laufzeitende."
                    />
                    <PeriodPicker
                      label="Verlängerung um"
                      required
                      presets={RENEWAL_PRESETS}
                      units={RENEWAL_TERM_UNITS}
                      value={renewal}
                      onChange={setRenewal}
                      hint="Um diese Dauer verlängert sich der Vertrag jeweils."
                    />
                  </div>
                ) : null}
              </div>
              )}

              <p className="mt-4 rounded-lg bg-accent/5 px-3 py-2 text-sm text-label">
                <span className="font-medium text-accent">Zusammengefasst: </span>
                {runtimeSentence}.
              </p>
            </Section>

            <Section title="Weitere Optionen" optional>
              <Checkbox
                id="vertrag-plan"
                label="Objekte müssen auf einem Plan platziert sein"
                description="Eine Wartung kann erst gestartet werden, wenn das Objekt auf dem Plan des Unterorts markiert wurde."
                checked={planPlacement}
                onChange={(e) => setPlanPlacement(e.target.checked)}
              />

              <details className="group mt-4 rounded-lg border border-separator">
                <summary className="cursor-pointer select-none px-3 py-2.5">
                  <span className="text-sm font-medium text-label">Formularzuweisungen je Ort</span>
                  <span className="block text-xs text-label-secondary">
                    Nur nötig, wenn an einem Ort ein anderes als das Standardformular der Kategorie verwendet werden soll.
                  </span>
                </summary>
                <div className="border-t border-separator p-3">
                  {mappingRows.length === 0 ? (
                    <div className="rounded-lg bg-fill-tertiary p-3 text-sm text-label-secondary">
                      Wähle oben mindestens einen Ort und eine Wartungskategorie, um Formulare je Ort zuweisen zu können.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {mappingRows.map((row) => {
                        const matchingForms = availableForms.filter((f) => String(f.maintenance_type_key || "") === row.typeKey);
                        const selected = formMappings.get(mappingKey(row.locationId, row.typeKey)) || "";
                        return (
                          <div
                            key={`${row.locationId}-${row.typeKey}`}
                            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-separator p-3"
                          >
                            <div className="min-w-0">
                              <div className="truncate text-sm font-medium text-label">{row.locationLabel}</div>
                              <div className="text-xs text-label-tertiary">{row.typeLabel}</div>
                            </div>
                            <select
                              className="eh-input w-64 max-w-full"
                              value={selected}
                              onChange={(e) => setMapping(row.locationId, row.typeKey, e.target.value)}
                            >
                              <option value="">Standardformular der Kategorie</option>
                              {matchingForms.map((f) => (
                                <option key={String(f.form_id)} value={String(f.form_id)}>
                                  {String(f.form_name || `Formular #${f.form_id}`)}
                                  {f.is_default ? " (Standard)" : ""}
                                </option>
                              ))}
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </details>
            </Section>

            {isEdit ? (
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  disabled={saving}
                  onClick={handleDelete}
                  className="text-status-red"
                  leftIcon={<TrashIcon className="h-4 w-4" aria-hidden />}
                >
                  {isOnCall ? "Eintrag löschen" : "Vertrag löschen"}
                </Button>
              </div>
            ) : null}
          </form>

          <aside className="space-y-4 lg:sticky lg:top-[calc(var(--eh-site-header-bottom,5.25rem)+0.75rem)] lg:z-10 lg:max-h-[calc(100dvh-var(--eh-site-header-bottom,5.25rem)-var(--eh-site-footer-height,2.5rem)-1.5rem)] lg:overflow-y-auto">
            <section className="rounded-ios border border-separator bg-card p-4 shadow-ios">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="truncate text-base font-semibold text-label" title={displayTitle}>
                    {displayTitle}
                  </h3>
                  {!isOnCall && contractNumber.trim() && contractNumber.trim() !== displayTitle ? (
                    <p className="text-xs text-label-tertiary">Nr. {contractNumber.trim()}</p>
                  ) : isOnCall ? (
                    <p className="text-xs text-label-tertiary">Auf Zuruf</p>
                  ) : null}
                </div>
                <ContractStatusBadge status={status} />
              </div>
              <dl className="mt-3 space-y-1.5">
                <SummaryRow label="Kunde" value={heroCustomerName || "–"} />
                <SummaryRow
                  label={selectedLocations.length === 1 ? "Ort" : "Orte"}
                  value={selectedLocations.length ? selectedLocations.map((l) => l.label).join(", ") : "–"}
                />
                <SummaryRow
                  label="Kategorien"
                  value={
                    selectedTypeKeys.length
                      ? selectedTypeKeys
                          .map((k) => maintenanceTypes.find((t) => String(t.key) === k)?.name || k)
                          .join(", ")
                      : "–"
                  }
                />
                <SummaryRow label="Rhythmus" value={frequencyLabel(interval)} />
                {isOnCall ? null : (
                  <SummaryRow
                    label="Laufzeit"
                    value={
                      startDate || endDate
                        ? `${startDate ? formatDate(startDate) : "…"} – ${endDate ? formatDate(endDate) : "offen"}`
                        : "unbefristet"
                    }
                  />
                )}
                {!isOnCall && autoRenew ? (
                  <SummaryRow
                    label="Verlängerung"
                    value={`${renewal ? durationLabel(renewal) : "–"} · Frist ${notice ? durationLabel(notice) : "–"}`}
                  />
                ) : null}
              </dl>
              <ul className="mt-4 space-y-1 border-t border-separator pt-3">
                <ChecklistItem done={customerDone} label="Kunde gewählt" />
                <ChecklistItem done={locationsDone} label={selectedLocations.length > 1 ? `${selectedLocations.length} Orte` : "Ort hinzugefügt"} />
                <ChecklistItem done={typesDone} label={selectedTypeKeys.length > 1 ? `${selectedTypeKeys.length} Kategorien` : "Kategorie gewählt"} />
                <ChecklistItem done={intervalDone && renewalDone && dateOrderOk} label={isOnCall ? "Rhythmus" : "Laufzeit & Rhythmus"} />
              </ul>
              <div className="mt-4 hidden lg:block">{saveButton}</div>
            </section>

            {isOnCall ? null : (
              <ContractDocumentCard
                contractId={contractId}
                document={document}
                pendingFile={pendingFile}
                onPendingFileChange={setPendingFile}
                onUpload={handleDocumentUpload}
                onRemove={handleDocumentRemove}
                busy={documentBusy || saving}
              />
            )}
          </aside>

          <div className="fixed inset-x-0 bottom-0 z-10 border-t border-separator bg-card/95 px-4 py-3 shadow-ios-md backdrop-blur lg:hidden">
            <div className="mx-auto flex max-w-screen-xl items-center justify-between gap-3">
              <span className="truncate text-xs text-label-secondary">{blockingHint || "Bereit zum Speichern."}</span>
              {saveButton}
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
